import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { getRestaurant } from '../lib/api';
import { ArrowLeft, Download, Printer, ChefHat } from 'lucide-react';
import { toast } from 'sonner';

export default function QRPoster() {
    const { id } = useParams();
    const navigate = useNavigate();
    const posterRef = useRef(null);
    const [restaurant, setRestaurant] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRestaurant = async () => {
            try {
                const response = await getRestaurant(id);
                setRestaurant(response.data);
            } catch (err) {
                toast.error('Failed to load restaurant');
                navigate('/dashboard/restaurants');
            } finally {
                setLoading(false);
            }
        };
        fetchRestaurant();
    }, [id, navigate]);

    const menuUrl = restaurant ? `${window.location.origin}/menu/${restaurant.slug}` : '';
    const primaryColor = restaurant?.primary_color || '#C9A227';
    const bgColor = restaurant?.background_color || '#0D0D0D';

    const downloadQR = () => {
        const canvas = posterRef.current?.querySelector('canvas');
        if (!canvas) {
            toast.error('QR code not ready');
            return;
        }
        const link = document.createElement('a');
        link.download = `${restaurant?.slug || 'menu'}-qr-code.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('QR code downloaded!');
    };

    const downloadPoster = () => {
        // Use html2canvas to capture the full poster
        import('html2canvas').then(({ default: html2canvas }) => {
            const poster = document.getElementById('qr-poster');
            if (!poster) return;
            
            html2canvas(poster, { 
                scale: 3,
                backgroundColor: bgColor,
                useCORS: true
            }).then(canvas => {
                const link = document.createElement('a');
                link.download = `${restaurant?.slug || 'menu'}-poster.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                toast.success('Poster downloaded!');
            });
        });
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F3E9]">
                <div className="w-10 h-10 border-4 border-[#C9A227] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!restaurant) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F3E9]">
                <p className="text-[#69665B]">Restaurant not found</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F3E9]">
            {/* Header - Hidden in print */}
            <header className="print:hidden sticky top-0 z-50 bg-white border-b border-[#E5E1D1] px-4 py-3">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate(`/dashboard/restaurants/${id}`)}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="font-semibold text-[#2B2A27]">QR Code Poster</h1>
                            <p className="text-sm text-[#969282]">{restaurant.name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            onClick={downloadQR}
                            className="gap-2 rounded-full"
                            data-testid="download-qr-btn"
                        >
                            <Download className="w-4 h-4" /> QR Only
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={downloadPoster}
                            className="gap-2 rounded-full"
                            data-testid="download-poster-btn"
                        >
                            <Download className="w-4 h-4" /> Full Poster
                        </Button>
                        <Button 
                            onClick={handlePrint}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white gap-2 rounded-full"
                            data-testid="print-btn"
                        >
                            <Printer className="w-4 h-4" /> Print
                        </Button>
                    </div>
                </div>
            </header>

            {/* Poster Preview */}
            <main className="p-6 print:p-0">
                <div className="max-w-lg mx-auto">
                    {/* Print-friendly poster */}
                    <div 
                        id="qr-poster"
                        ref={posterRef}
                        className="aspect-[3/4] rounded-2xl print:rounded-none overflow-hidden shadow-2xl print:shadow-none"
                        style={{ backgroundColor: bgColor }}
                    >
                        <div className="h-full flex flex-col items-center justify-center p-8 sm:p-12 text-center">
                            {/* Logo */}
                            <div className="mb-6">
                                {restaurant.logo_url ? (
                                    <img 
                                        src={restaurant.logo_url} 
                                        alt={restaurant.name}
                                        className="w-24 h-24 sm:w-32 sm:h-32 object-contain mx-auto rounded-2xl"
                                        crossOrigin="anonymous"
                                    />
                                ) : (
                                    <div 
                                        className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl mx-auto flex items-center justify-center"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        <ChefHat className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
                                    </div>
                                )}
                            </div>

                            {/* Restaurant Name */}
                            <h1 
                                className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-2"
                                style={{ color: primaryColor }}
                            >
                                {restaurant.name}
                            </h1>

                            {/* Tagline */}
                            <p 
                                className="text-base sm:text-lg mb-8 opacity-70"
                                style={{ color: primaryColor }}
                            >
                                Scan to view our menu in AR
                            </p>

                            {/* QR Code */}
                            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg mb-8">
                                <QRCodeCanvas
                                    value={menuUrl}
                                    size={200}
                                    level="H"
                                    fgColor={primaryColor}
                                    includeMargin={false}
                                />
                            </div>

                            {/* URL */}
                            <p 
                                className="text-xs sm:text-sm opacity-50 font-mono"
                                style={{ color: primaryColor }}
                            >
                                {menuUrl.replace('https://', '').replace('http://', '')}
                            </p>

                            {/* Powered by */}
                            <p 
                                className="mt-auto pt-8 text-xs opacity-30"
                                style={{ color: primaryColor }}
                            >
                                Made with Scano
                            </p>
                        </div>
                    </div>

                    {/* Instructions - Hidden in print */}
                    <div className="print:hidden mt-8 p-6 bg-white rounded-xl border border-[#E5E1D1]">
                        <h3 className="font-medium text-[#2B2A27] mb-3">📋 Printing Tips</h3>
                        <ul className="text-sm text-[#69665B] space-y-2">
                            <li>• Print at A4 or Letter size for best results</li>
                            <li>• Use high-quality paper or card stock</li>
                            <li>• Laminate for durability in restaurant environments</li>
                            <li>• Place at eye level on tables or at entrance</li>
                            <li>• QR code should be at least 3x3 cm for easy scanning</li>
                        </ul>
                    </div>
                </div>
            </main>

            {/* Print Styles */}
            <style>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 0;
                    }
                    
                    body {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    
                    #qr-poster {
                        width: 100vw !important;
                        height: 100vh !important;
                        max-width: none !important;
                        aspect-ratio: auto !important;
                        border-radius: 0 !important;
                    }
                }
            `}</style>
        </div>
    );
}
